// Settings (IMPLEMENTATION §3 M7, §2.6 "Settings: Hs − HUD", UX §2 Settings / §7): Audio (Master, Sound
// effects, Mute, "Respect silent switch" = iOS ambient session), Comfort (Reduce motion System / On / Off, Low
// quality = DPR 1.5 + 30 fps + fewer particles, Hints), Data (Export telemetry: share → download → copy;
// Reset save: hold settings.resetHoldMs with a countdown ring → a fresh cold open), About (version · build —
// 7 quick taps open the DebugPanel, L14 / M12 #6 —, licences, Replay intro). Every change is applied at once
// (the M9 audio facade, the renderer quality, fx/motion) and saved in the save's meta.settings.
import { useEffect, useRef, useState } from 'preact/hooks';
import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { settingsRect } from '../panels.ts';
import { type ReduceMotionMode, type Settings, settingsSig, updateSettings } from '../settings.ts';
import { copyTextSig, layoutSig, panelSig } from '../state.ts';
import { box } from './box.ts';

/**
 * The Export telemetry fallback (M12 exportFile: share → download → copy): the CSV in a selectable box. Shown
 * wherever the export was started (Settings, the completion banner) while `copyTextSig` holds the text.
 */
export function CopyBox() {
  const text = copyTextSig.value;
  if (text === null) return null;
  const c = shellCtx().data().copy.settings;
  return (
    <div class="copy-box" data-testid="copy-box">
      <textarea readOnly value={text} class="ctl" />
      <button
        type="button"
        class="btn ctl"
        data-hit="copy-close"
        onClick={() => {
          copyTextSig.value = null;
        }}
      >
        {c.close ?? ''}
      </button>
    </div>
  );
}

function change(patch: Partial<Settings>): void {
  updateSettings(patch);
  shellCtx().actions.settings(settingsSig.value);
}

function Toggle(props: { label: string; on: boolean; hit: string; onChange(v: boolean): void }) {
  return (
    <label class="set-row">
      <span>{props.label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={props.on}
        class={`switch ctl ${props.on ? 'on' : ''}`}
        data-hit={props.hit}
        onClick={() => props.onChange(!props.on)}
      >
        <i />
      </button>
    </label>
  );
}

function Slider(props: {
  label: string;
  value: number;
  step: number;
  hit: string;
  onChange(v: number): void;
}) {
  const copy = shellCtx().data().copy.settings;
  return (
    <label class="set-row">
      <span>{props.label}</span>
      <input
        type="range"
        class="slider ctl"
        data-hit={props.hit}
        min={0}
        max={1}
        step={props.step}
        value={props.value}
        onInput={(e) => props.onChange(Number((e.currentTarget as HTMLInputElement).value))}
      />
      <span class="set-val">
        {fillCopy(copy.volume ?? '{n}%', { n: Math.round(props.value * 100) })}
      </span>
    </label>
  );
}

function ResetHold() {
  const ctx = shellCtx();
  const copy = ctx.data().copy.settings;
  const holdMs = ctx.data().ui.settings.resetHoldMs;
  const [pct, setPct] = useState(0);
  const t0 = useRef<number | null>(null);
  const raf = useRef(0);
  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  const stop = (): void => {
    t0.current = null;
    cancelAnimationFrame(raf.current);
    setPct(0);
  };
  const down = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    t0.current = performance.now();
    const step = (now: number): void => {
      if (t0.current === null) return;
      const k = Math.min(1, (now - t0.current) / holdMs);
      setPct(Math.round(k * 100));
      if (k >= 1) {
        stop();
        ctx.actions.resetSave();
        return;
      }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  };
  const left = Math.ceil(((100 - pct) / 100) * (holdMs / 1000));
  return (
    <button
      type="button"
      class="btn btn-danger hold ctl"
      data-hit="reset-save"
      data-testid="reset-save"
      style={{ '--hold': `${pct}%` }}
      onPointerDown={down}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={stop}
    >
      <span class="ring" style={{ '--hold': `${pct}%` }} />
      {copy.reset ?? ''}
      {pct > 0 ? ` · ${left}` : ''}
    </button>
  );
}

export function SettingsSheet() {
  const l = layoutSig.value;
  const s = settingsSig.value;
  const [exported, setExported] = useState<string | null>(null);
  if (!l || panelSig.value.k !== 'settings') return null;
  const ctx = shellCtx();
  const c = ctx.data().copy.settings;
  const step = ctx.data().ui.settings.volumeStep;
  const rm = (m: ReduceMotionMode): void => change({ reduceMotion: m });
  return (
    <div class="settings ctl" style={box(settingsRect(l))} data-testid="settings">
      <div class="sheet-head">
        <span class="sheet-title">{c.title ?? ''}</span>
        <button
          type="button"
          class="icon-btn ctl"
          data-hit="settings-close"
          aria-label={c.close ?? ''}
          onClick={() => ctx.shell.closePanel()}
        >
          ✕
        </button>
      </div>
      <div class="settings-body">
        <h3>{c.audio ?? ''}</h3>
        <Slider
          label={c.master ?? ''}
          value={s.master}
          step={step}
          hit="set-master"
          onChange={(v) => change({ master: v })}
        />
        <Slider
          label={c.sfx ?? ''}
          value={s.sfx}
          step={step}
          hit="set-sfx"
          onChange={(v) => change({ sfx: v })}
        />
        <Toggle
          label={c.mute ?? ''}
          on={s.muted}
          hit="set-mute"
          onChange={(v) => change({ muted: v })}
        />
        <Toggle
          label={c.silentSwitch ?? ''}
          on={s.silentSwitch === 'ambient'}
          hit="set-silent"
          onChange={(v) => change({ silentSwitch: v ? 'ambient' : 'playback' })}
        />
        <h3>{c.comfort ?? ''}</h3>
        <div class="set-row">
          <span>{c.reduceMotion ?? ''}</span>
          <div class="seg">
            {(['system', 'on', 'off'] as const).map((m) => (
              <button
                key={m}
                type="button"
                class={`seg-btn ctl ${s.reduceMotion === m ? 'on' : ''}`}
                data-hit={`set-rm-${m}`}
                aria-pressed={s.reduceMotion === m}
                onClick={() => rm(m)}
              >
                {m === 'system'
                  ? (c.reduceMotionSystem ?? '')
                  : m === 'on'
                    ? (c.on ?? '')
                    : (c.off ?? '')}
              </button>
            ))}
          </div>
        </div>
        <Toggle
          label={c.lowQuality ?? ''}
          on={s.lowQuality}
          hit="set-low"
          onChange={(v) => change({ lowQuality: v })}
        />
        <Toggle
          label={c.hints ?? ''}
          on={s.hints}
          hit="set-hints"
          onChange={(v) => change({ hints: v })}
        />
        <h3>{c.data ?? ''}</h3>
        <div class="set-row">
          <button
            type="button"
            class="btn ctl"
            data-hit="export-telemetry"
            data-testid="export-telemetry"
            onClick={() =>
              void ctx.actions
                .exportTelemetry()
                .then((how) => setExported(fillCopy(c.exported ?? '{how}', { how })))
            }
          >
            {c.exportTelemetry ?? ''}
          </button>
          {exported !== null ? <span class="set-val">{exported}</span> : null}
        </div>
        <CopyBox />
        <div class="set-row">
          <ResetHold />
        </div>
        <h3>{c.about ?? ''}</h3>
        <p class="set-version" data-testid="version-label">
          {fillCopy(c.version ?? '{version}', { version: ctx.version, build: ctx.buildId })}
        </p>
        <p class="set-licences">
          <b>{c.licences ?? ''}:</b> {c.licenceList ?? ''}
        </p>
        <div class="set-row">
          <button
            type="button"
            class="btn ctl"
            data-hit="replay-intro"
            onClick={() => {
              ctx.actions.click();
              ctx.shell.replayIntro();
            }}
          >
            {c.replayIntro ?? ''}
          </button>
        </div>
      </div>
    </div>
  );
}
